/**
 * UranIA PA — Form creazione/aggiornamento versione skill (admin).
 *
 * Riutilizzabile per:
 *   - Creazione di una skill nuova (``mode='create'``, l'admin sceglie
 *     anche ``skill_id`` rispettando il pattern ``^[a-z][a-z0-9_]*$``).
 *   - Aggiunta di una nuova versione a una skill esistente
 *     (``mode='new-version'``, ``skill_id`` bloccato in read-only).
 *
 * Boundary:
 *   - In dev locale lo step-up MFA e' bypassato dalla flag env
 *     ``URANIA_SKIP_STEP_UP=true`` sul ``skills-registry``. In prod
 *     l'admin riceve 401 con ``acr_required`` e deve ri-autenticarsi.
 *   - Il ``body`` (prompt cifrato AES-256-GCM lato server) e' una
 *     textarea. La cifratura e l'audit AgID sono server-side.
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JSX, FormEvent } from 'react';

import {
  createOrUpdateSkillVersion,
  SkillsApiError,
} from '../../lib/admin/skills-api.ts';
import type {
  CreateSkillVersionRequestDto,
  CreateSkillVersionResponseDto,
} from '../../lib/admin/skills-api.ts';

export type SkillVersionFormMode = 'create' | 'new-version';

export interface SkillVersionFormProps {
  readonly mode: SkillVersionFormMode;
  /** In modalita' ``new-version`` lo skill_id e' fissato; in ``create`` opzionale (default-empty). */
  readonly defaultSkillId?: string;
  readonly defaultName?: string;
  readonly defaultDescription?: string | null;
  readonly defaultIsCore?: boolean;
  readonly defaultTenantId?: string | null;
  /** Callback invocata al successo (typed response). */
  readonly onSuccess?: (response: CreateSkillVersionResponseDto) => void;
}

const SKILL_ID_PATTERN = /^[a-z][a-z0-9_]*$/u;
const SEMVER_PATTERN = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/u;

export function SkillVersionForm(props: SkillVersionFormProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const fieldIds = {
    skill_id: useId(),
    version: useId(),
    name: useId(),
    description: useId(),
    body: useId(),
    is_core: useId(),
    tenant_id: useId(),
  };

  const [skillId, setSkillId] = useState(props.defaultSkillId ?? '');
  const [version, setVersion] = useState('1.0.0');
  const [name, setName] = useState(props.defaultName ?? '');
  const [description, setDescription] = useState(props.defaultDescription ?? '');
  const [bodyText, setBodyText] = useState('');
  const [isCore, setIsCore] = useState(props.defaultIsCore ?? true);
  const [tenantId, setTenantId] = useState(props.defaultTenantId ?? '');

  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const mutation = useMutation<
    CreateSkillVersionResponseDto,
    SkillsApiError,
    { skillId: string; payload: CreateSkillVersionRequestDto }
  >({
    mutationFn: async ({ skillId: id, payload }) =>
      createOrUpdateSkillVersion(id, payload),
    onSuccess: (response) => {
      setSuccessMessage(
        response.audit_action === 'created'
          ? t('admin.skills.form.success_created', {
              skillId: response.skill_id,
              version: response.version,
            })
          : t('admin.skills.form.success_updated', {
              skillId: response.skill_id,
              version: response.version,
            }),
      );
      void queryClient.invalidateQueries({ queryKey: ['admin', 'skills'] });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'skill-detail', response.skill_id],
      });
      props.onSuccess?.(response);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);
    setSuccessMessage(null);

    if (!SKILL_ID_PATTERN.test(skillId)) {
      setValidationError(t('admin.skills.form.errors.invalid_skill_id'));
      return;
    }
    if (!SEMVER_PATTERN.test(version)) {
      setValidationError(t('admin.skills.form.errors.invalid_version'));
      return;
    }
    if (name.trim().length === 0) {
      setValidationError(t('admin.skills.form.errors.name_required'));
      return;
    }
    if (bodyText.trim().length === 0) {
      setValidationError(t('admin.skills.form.errors.body_required'));
      return;
    }

    const payload: CreateSkillVersionRequestDto = {
      version,
      name: name.trim(),
      description: description.trim().length > 0 ? description.trim() : null,
      body: bodyText,
      tenant_id: isCore ? null : tenantId.trim().length > 0 ? tenantId.trim() : null,
      is_core: isCore,
    };
    mutation.mutate({ skillId, payload });
  }

  const isLocked = props.mode === 'new-version';
  const apiError = mutation.error;

  return (
    <form onSubmit={handleSubmit} data-testid="admin-skill-form" noValidate>
      <div className="row g-3">
        <div className="col-md-7">
          <label htmlFor={fieldIds.skill_id} className="form-label fw-semibold">
            {t('admin.skills.form.fields.skill_id')}
          </label>
          <input
            id={fieldIds.skill_id}
            type="text"
            className="form-control font-monospace"
            value={skillId}
            onChange={(e): void => setSkillId(e.target.value.toLowerCase())}
            placeholder="skill_nome_breve"
            required
            disabled={isLocked || mutation.isPending}
            pattern="^[a-z][a-z0-9_]*$"
            data-testid="admin-skill-form-skill-id"
          />
          <div className="form-text">{t('admin.skills.form.fields.skill_id_hint')}</div>
        </div>
        <div className="col-md-5">
          <label htmlFor={fieldIds.version} className="form-label fw-semibold">
            {t('admin.skills.form.fields.version')}
          </label>
          <input
            id={fieldIds.version}
            type="text"
            className="form-control font-monospace"
            value={version}
            onChange={(e): void => setVersion(e.target.value)}
            placeholder="1.0.0"
            required
            disabled={mutation.isPending}
            data-testid="admin-skill-form-version"
          />
          <div className="form-text">{t('admin.skills.form.fields.version_hint')}</div>
        </div>
        <div className="col-12">
          <label htmlFor={fieldIds.name} className="form-label fw-semibold">
            {t('admin.skills.form.fields.name')}
          </label>
          <input
            id={fieldIds.name}
            type="text"
            className="form-control"
            value={name}
            onChange={(e): void => setName(e.target.value)}
            placeholder={t('admin.skills.form.fields.name_placeholder')}
            required
            disabled={mutation.isPending}
            data-testid="admin-skill-form-name"
          />
        </div>
        <div className="col-12">
          <label htmlFor={fieldIds.description} className="form-label">
            {t('admin.skills.form.fields.description')}
          </label>
          <input
            id={fieldIds.description}
            type="text"
            className="form-control"
            value={description}
            onChange={(e): void => setDescription(e.target.value)}
            placeholder={t('admin.skills.form.fields.description_placeholder')}
            disabled={mutation.isPending}
            data-testid="admin-skill-form-description"
          />
        </div>
        <div className="col-12">
          <label htmlFor={fieldIds.body} className="form-label fw-semibold">
            {t('admin.skills.form.fields.body')}
          </label>
          <textarea
            id={fieldIds.body}
            className="form-control font-monospace"
            rows={10}
            value={bodyText}
            onChange={(e): void => setBodyText(e.target.value)}
            placeholder={t('admin.skills.form.fields.body_placeholder')}
            required
            disabled={mutation.isPending}
            data-testid="admin-skill-form-body"
          />
          <div className="form-text">{t('admin.skills.form.fields.body_hint')}</div>
        </div>
        <div className="col-md-4">
          <div className="form-check pt-2">
            <input
              id={fieldIds.is_core}
              type="checkbox"
              className="form-check-input"
              checked={isCore}
              onChange={(e): void => setIsCore(e.target.checked)}
              disabled={mutation.isPending}
              data-testid="admin-skill-form-is-core"
            />
            <label htmlFor={fieldIds.is_core} className="form-check-label">
              {t('admin.skills.form.fields.is_core')}
            </label>
            <div className="form-text">{t('admin.skills.form.fields.is_core_hint')}</div>
          </div>
        </div>
        <div className="col-md-8">
          <label htmlFor={fieldIds.tenant_id} className="form-label">
            {t('admin.skills.form.fields.tenant_id')}
          </label>
          <input
            id={fieldIds.tenant_id}
            type="text"
            className="form-control font-monospace"
            value={tenantId}
            onChange={(e): void => setTenantId(e.target.value)}
            placeholder="2PWBQCYW2TQFDQ8F854B5GWZHC"
            disabled={isCore || mutation.isPending}
            data-testid="admin-skill-form-tenant-id"
          />
          <div className="form-text">{t('admin.skills.form.fields.tenant_id_hint')}</div>
        </div>
      </div>

      {validationError !== null && (
        <div
          className="alert alert-warning mt-3 mb-0"
          role="alert"
          data-testid="admin-skill-form-validation-error"
        >
          {validationError}
        </div>
      )}

      {apiError !== null && apiError instanceof SkillsApiError && (
        <div
          className="alert alert-danger mt-3 mb-0"
          role="alert"
          data-testid="admin-skill-form-api-error"
        >
          <strong>{apiError.message}</strong>
          {apiError.status === 401 && apiError.acrRequired !== null && (
            <p className="mb-0 small mt-1">
              {t('admin.skills.form.errors.step_up_required', {
                acr: apiError.acrRequired.join(', '),
              })}
            </p>
          )}
          {apiError.status === 403 && (
            <p className="mb-0 small mt-1">
              {t('admin.skills.form.errors.permission_denied')}
            </p>
          )}
          {apiError.status === 409 && (
            <p className="mb-0 small mt-1">
              {t('admin.skills.form.errors.version_conflict')}
            </p>
          )}
          {apiError.detail !== null && (
            <p className="mb-0 small mt-1 text-muted">{apiError.detail}</p>
          )}
        </div>
      )}

      {successMessage !== null && (
        <div
          className="alert alert-success mt-3 mb-0"
          role="status"
          data-testid="admin-skill-form-success"
        >
          {successMessage}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={mutation.isPending}
          data-testid="admin-skill-form-submit"
        >
          {mutation.isPending
            ? t('admin.skills.form.submitting')
            : props.mode === 'create'
              ? t('admin.skills.form.submit_create')
              : t('admin.skills.form.submit_new_version')}
        </button>
      </div>
    </form>
  );
}
